import { Component, ViewChild, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core'
import { FormBuilder } from '@angular/forms'
import { finalize } from 'rxjs/operators'
import { UiService } from 'src/app/services/ui.service'
import { AccountService } from '../../services/account.service'
import { ModalDialogComponent } from 'src/app/shared/modal-dialog/modal-dialog.component'
import { ApiService } from 'src/app/services/api.service'
import { AccessLevelService } from 'src/app/services/access-level.service'
import { RoleLoadingService } from 'src/app/services/role-loading.service'
import { ApiKeyDto } from 'src/app/services/dtos'
import { ModalService, ModalController } from '../../services/modal.service'
import { handleArpioError } from 'src/utils/handle-error'
import { ApiKeyModalBaseComponent } from '../api-key-modal-base/api-key-modal-base.component'
import { ClipboardService } from 'src/app/services/clipboard.service'
import { CopyFeedbackService } from 'src/app/shared/copy-feedback.service'

@Component({
  selector: 'app-new-api-key-modal',
  templateUrl: './new-api-key-modal.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [CopyFeedbackService],
})
export class NewApiKeyModalComponent extends ApiKeyModalBaseComponent implements ModalController {
  @ViewChild('newApiKeyModal') modal: ModalDialogComponent
  @ViewChild('secretDialog') secretDialog: ModalDialogComponent
  @Output() apiKeyCreated = new EventEmitter<void>()

  // For displaying the generated API key secret
  createdApiKey: ApiKeyDto | null = null

  constructor(
    formBuilder: FormBuilder,
    accountService: AccountService,
    uiService: UiService,
    apiService: ApiService,
    accessLevelService: AccessLevelService,
    roleLoadingService: RoleLoadingService,
    private modalService: ModalService,
    private clipboard: ClipboardService,
    public copyFeedback: CopyFeedbackService
  ) {
    super(formBuilder, accountService, uiService, apiService, accessLevelService, roleLoadingService)
    this.modalService.register(this, this)
  }

  ngOnDestroy() {
    super.ngOnDestroy()
    this.modalService.unregister(this)
  }

  async open() {
    this.resetForm()
    await this.loadAvailableRoles()
    this.modal.open()
  }

  protected resetForm() {
    super.resetForm()
    this.createdApiKey = null
  }

  // Save operation
  onSave() {
    if (!this.isFormValid || this.saving) {
      return
    }

    this.saving = true

    const formValue = this.apiKeyForm.value
    const apiKeyPayload: ApiKeyDto = {
      name: formValue.name,
      enabled: formValue.enabled !== false,
      roles: this.selectedRoles.map((r) => r.id),
    }

    this.saveSubscription = this.apiService
      .createApiKey(this.accountService.accountId, apiKeyPayload)
      .pipe(
        finalize(() => {
          this.saving = false
        })
      )
      .subscribe(
        (result: ApiKeyDto) => {
          this.createdApiKey = result
          this.modal.close()
          this.secretDialog.open()
          this.apiKeyCreated.emit()
        },
        handleArpioError(window.location.href, this.apiService, this.uiService, {
          errorContext: { dialogId: 'new-api-key-modal' },
        })
      )
  }

  // Close secret dialog and the main modal
  closeSecretDialog() {
    this.secretDialog.close()
    this.close()
  }

  copySecretToClipboard() {
    if (!this.createdApiKey?.secret) return
    this.clipboard.copy(this.createdApiKey.secret).then((ok) => {
      if (ok) this.copyFeedback.onCopied('secret')
    })
  }

  getSaveButtonText(): string {
    return this.saving ? 'Creating...' : 'Create API Key'
  }
}
